<template>
  <div
    data-testid="sf-markdown-editor"
    class="
      tw-flex
      tw-flex-col
      tw-rounded
      tw-border
      tw-border-solid
      tw-border-sf-snow-darker
    "
    :class="editorWrapperClasses"
  >
    <SfMarkdownToolbar
      v-if="editor"
      :editor="editor"
      :hide-linking="hideContentLinking"
      :hide-mention="disableMentions"
    />
    <EditorContent
      v-if="editor"
      :editor="editor"
      class="tw-overflow-y-auto tw-flex-1 tw-p-3 sf-layer-white"
      :class="contentWrapperClasses"
    />
  </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

import { useEditor, EditorContent } from '@tiptap/vue-3'

import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import { Mention } from '@tiptap/extension-mention'
import { ListItem, OrderedList, BulletList } from '@tiptap/extension-list'
import HardBreak from '@tiptap/extension-hard-break'
import Strike from '@tiptap/extension-strike'
import Blockquote from '@tiptap/extension-blockquote'
import { UndoRedo, Placeholder } from '@tiptap/extensions'
import Link from '@tiptap/extension-link'
import { marked } from 'marked'
import TurndownService from 'turndown'

import sanitize from '@app/adapters/sanitizer'

import SelectWord from './extensions/select-word'
import MentionDecoration from './extensions/mention-decoration'
import LiquidDecoration from './extensions/liquid-decoration'
import { MENTION_PATTERN } from './mentions'
import { parseLiquidToHtml, liquidAwareEscape } from './liquid'
import SfMarkdownToolbar from './sf-markdown-toolbar.vue'
import SfMarkdownMentionList from './sf-markdown-mention-list.vue'
import SfMarkdownDocumentLinkPicker from './sf-markdown-document-link-picker.vue'
import { createSuggestionRenderer } from './suggestion-renderer-factory'
import type { MentionItem, SuggestionRenderer } from './types'
import { createSuggestionWatch } from './utils'
import type { SuggestionProps } from '@tiptap/suggestion'
import type { DocumentTypeValue } from './constants'
import { PrefixMapping } from './constants'
import type { Attribute } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

interface Props {
  modelValue: string | null | undefined
  mentionUsernames: string[]
  mentionAccounts: MentionItem[]
  mentionReconTemplates?: MentionItem[]
  isLoadingAccounts?: boolean
  isLoadingReconTemplates?: boolean
  squareBottom?: boolean
  placeholder?: string
  disableMentions?: boolean
  disableContentLinking?: boolean
  disabled?: boolean
  hasError?: boolean
  autofocus?: boolean
  enableLiquid?: boolean
  pulseUsernames?: string[]
  pulseWarningActive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  mentionList: () => [],
  mentionReconTemplates: () => [],
  placeholder: undefined,
  modelValue: undefined,
  isLoadingAccounts: false,
  isLoadingReconTemplates: false,
  disableMentions: false,
  disableContentLinking: false,
  disabled: false,
  hasError: false,
  autofocus: false,
  enableLiquid: false,
  pulseUsernames: () => [],
  pulseWarningActive: false,
})

const emit =
  defineEmits<{
    'update:modelValue': [value: string]
  }>()

const markdownModel = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

const isPulseUser = window.Silverfin?.isCurrentUserPulse?.() ?? false

// Content linking (#, !, /) is unavailable when the caller opts out (e.g. the
// firm-level bulk/trigger message modals, where the client file to link
// against is unknown) or for Pulse users (no access to linkable items).
const hideContentLinking = computed(
  () => isPulseUser || props.disableContentLinking
)

const filteredUsers = ({ query }: { query: string }): MentionItem[] => {
  return props.mentionUsernames.reduce<MentionItem[]>((acc, item) => {
    if (item.toLowerCase().includes(query.toLowerCase())) {
      acc.push({ id: item, type: 'username', link: 'noop' })
    }
    return acc
  }, [])
}

const filteredAccounts = ({ query }: { query: string }): MentionItem[] => {
  const searchQuery = query || ''
  return props.mentionAccounts
    .filter((item) => item.id.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.id.localeCompare(b.id))
}

const filteredReconTemplates = ({
  query,
}: {
  query: string
}): MentionItem[] => {
  const searchQuery = query || ''
  return props.mentionReconTemplates
    .filter((item) => item.id.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.id.localeCompare(b.id))
}

// change default markdown delimiter for italic from `_` to `*`
// otherwise backend transforms this to an underline <u> tag
const turndownService = new TurndownService({ emDelimiter: '*' })
const isMention = (node: HTMLElement, mentionChar: string) => {
  return (
    node.classList.contains('mention') &&
    node.dataset.type === 'mention' &&
    node.dataset.mentionSuggestionChar === mentionChar
  )
}

// Custom rule for mentioning users
turndownService.addRule('userMentions', {
  filter: (node) => isMention(node, '@'),
  replacement: (_content, node) => `@${node.textContent?.slice(1)}`,
})

// Custom rule for mentioning documents
turndownService.addRule('documentMentions', {
  filter: (node) => ['#', '!', '/'].some((char) => isMention(node, char)),
  replacement: (_content, node) => {
    const item = (node as HTMLElement).dataset
    const link = item.link
    const name = item.id
    const mentionType = item.mentionType as DocumentTypeValue
    const prefixChar = PrefixMapping[mentionType]

    return `<a href="${link}" target="_blank" rel="noopener noreferrer">${prefixChar}${name}</a>`
  },
})

// On Liquid-aware fields, keep the template source verbatim through turndown's
// text escaper instead of markdown-escaping it (see liquidAwareEscape). Scoped
// to enableLiquid so other editors keep turndown's default escaping.
if (props.enableLiquid) {
  const defaultEscape = turndownService.escape.bind(turndownService)
  turndownService.escape = (value: string): string =>
    liquidAwareEscape(value, defaultEscape)
}

const isFocused = ref(false)

// Helper function to create mention list renderers with consistent pattern
const createMentionListRenderer = (
  getIsLoading?: () => boolean,
  getFilteredItems?: (query: string) => MentionItem[]
) => {
  return createSuggestionRenderer({
    component: SfMarkdownMentionList,
    getProps: (suggestionProps: SuggestionProps) => {
      // If no custom item filter, pass through TipTap's default props
      if (!getFilteredItems) {
        return {}
      }
      // Otherwise, override with our custom items
      return {
        items: getFilteredItems(suggestionProps.query),
        isLoading: getIsLoading?.() || false,
      }
    },
    includeForceUpdate: !!getFilteredItems,
  })
}

// Create renderer for username mentions (@)
const usernameRenderer = createMentionListRenderer()

// Create renderer for account mentions (#)
const accountLinkRenderer = createMentionListRenderer(
  () => props.isLoadingAccounts,
  (query: string) => filteredAccounts({ query })
)

// Create renderer for reconciliation template mentions (!)
const reconTemplateLinkRenderer = createMentionListRenderer(
  () => props.isLoadingReconTemplates,
  (query: string) => filteredReconTemplates({ query })
)

// Create renderer for account, reconciliation template, document and export mentions (/)
const documentLinkRenderer = createSuggestionRenderer({
  component: SfMarkdownDocumentLinkPicker,
  getProps: () => ({}),
})

// Factory function to create a Mention extension variant with a specific trigger character
// NOTE: The interface below is a workaround fix for a bug in TipTap (https://github.com/ueberdosis/tiptap/issues/6670)
interface MentionAddAttributesThis {
  parent?: () => Record<string, Attribute>
}

const createMentionExtension = (
  name: string,
  char: string,
  items: (props: { query: string }) => MentionItem[],
  renderer: () => SuggestionRenderer
) => {
  return Mention.extend({
    name,
    addAttributes(this: MentionAddAttributesThis) {
      const baseAttributes: Record<string, Attribute> = {
        mentionType: {
          renderHTML: (attributes: Record<string, string | null>) => {
            return { 'data-mention-type': attributes.mentionType }
          },
        },
        link: {
          renderHTML: (attributes: Record<string, string | null>) => {
            return { 'data-link': attributes.link }
          },
        },
      }
      return { ...this.parent?.(), ...baseAttributes }
    },
    renderHTML({
      node,
      HTMLAttributes,
    }: {
      node: ProseMirrorNode
      HTMLAttributes: Record<string, string>
    }) {
      const { id, mentionType } = node.attrs as {
        id: string
        mentionType: DocumentTypeValue
      }
      const prefixChar = PrefixMapping[mentionType] || char

      return [
        'span',
        {
          // Amber Pulse styling is added reactively by the mentionDecoration plugin.
          class: 'mention',
          'data-type': 'mention',
          ...HTMLAttributes,
        },
        `${prefixChar}${id}`,
      ]
    },
  }).configure({
    suggestion: {
      char,
      items,
      render: renderer,
    },
  })
}

const editor = useEditor({
  editable: !props.disabled,
  // Focus when the editor mounts. Doing it here (rather than a caller invoking
  // autoFocus() on open) is reliable inside modals, where the editor is created
  // after the modal body renders.
  autofocus: props.autofocus ? 'end' : false,
  extensions: [
    Document,
    Paragraph,
    Text,
    Bold,
    Italic,
    ListItem,
    OrderedList,
    BulletList,
    HardBreak,
    Strike,
    Blockquote,
    UndoRedo,
    ...(props.disableMentions
      ? []
      : [
          createMentionExtension(
            'usernameMention',
            '@',
            filteredUsers,
            () => usernameRenderer
          ),
          // Suggestion-list chips only style picked mentions; this also
          // decorates typed-but-unselected mentions.
          MentionDecoration.configure({
            getMentionUsernames: () => props.mentionUsernames,
            getPulseUsernames: () => props.pulseUsernames,
            isPulseWarningActive: () => props.pulseWarningActive,
          }),
        ]),
    // Skip registering the extensions that back the `#`, `!`, `/` content-link
    // shortcuts when content linking is hidden (Pulse users lack access to the
    // linkable items — GL #31836 — and firm-level modals opt out via
    // disableContentLinking).
    ...(hideContentLinking.value
      ? []
      : [
          createMentionExtension(
            'accountMention',
            '#',
            () => [],
            () => accountLinkRenderer
          ),
          createMentionExtension(
            'reconciliationTemplateMention',
            '!',
            () => [],
            () => reconTemplateLinkRenderer
          ),
          createMentionExtension(
            'documentMention',
            '/',
            () => [],
            () => documentLinkRenderer
          ),
        ]),
    // Highlight Liquid ({{ }} / {% %}) inline while keeping it plain editable
    // text; the markdown round-trip preserves the source (see ./liquid.ts).
    ...(props.enableLiquid ? [LiquidDecoration] : []),
    Link.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          title: {
            default: null,
            parseHTML: (element) => element.getAttribute('title') || null,
            renderHTML: (attributes) => {
              if (!attributes.title) return {}
              return { title: attributes.title }
            },
          },
        }
      },
    }).configure({
      openOnClick: false,
      // Autolinking a URL that contains Liquid ({{ id }}) would split/encode it;
      // keep URLs literal on Liquid-aware fields.
      autolink: !props.enableLiquid,
      linkOnPaste: !props.enableLiquid,
      defaultProtocol: 'https',
      protocols: ['http', 'https', 'vclink'],
    }),
    SelectWord,
    ...(props.placeholder
      ? [
          Placeholder.configure({
            placeholder: props.placeholder,
          }),
        ]
      : []),
  ],
  onCreate: async ({ editor }) => {
    if (!markdownModel.value) {
      return
    }

    // On Liquid-aware fields, mask Liquid spans out before marked runs and
    // restore them as plain text, so the template source is never reflowed.
    const html = props.enableLiquid
      ? await parseLiquidToHtml(markdownModel.value)
      : await marked.parse(markdownModel.value)

    // match @mentions like @user123 or @abc-123 that are valid usernames
    // but ignore parts of emails like email@domain.com
    const htmlWithMentions = props.disableMentions
      ? html
      : html.replace(new RegExp(MENTION_PATTERN, 'gi'), (match, username) => {
          return props.mentionUsernames.includes(username.toLowerCase())
            ? `<span data-type="mention" data-id="${username}" class="mention" data-mention-suggestion-char="@">@${username}</span>`
            : match
        })

    // emitUpdate: false so mounting does not fire onUpdate and write turndown's
    // output back into the model. Otherwise an existing value round-trips through
    // marked/turndown on open (which can reflow whitespace — e.g. a multi-line
    // Liquid block collapsing onto one line), silently rewriting the stored
    // template when the user saves after editing only another field.
    editor.commands.setContent(sanitize(htmlWithMentions), {
      emitUpdate: false,
    })
  },
  onUpdate: ({ editor }) => {
    // Convert HTML to markdown
    emit('update:modelValue', turndownService.turndown(editor.getHTML()))
  },
  onFocus: () => {
    isFocused.value = true
  },
  onBlur: () => {
    isFocused.value = false
  },
})

// Toggle editing when the caller flips `disabled` (e.g. while a send is in
// flight), mirroring the disabled state the plain textarea used to expose.
watch(
  () => props.disabled,
  (disabled) => editor.value?.setEditable(!disabled)
)

// mentionDecoration only recomputes on a transaction, so dispatch an empty one
// when the Pulse props change after mount to restyle already-rendered chips.
watch(
  () => [props.pulseUsernames, props.pulseWarningActive],
  () => {
    const instance = editor.value
    if (instance) instance.view.dispatch(instance.state.tr)
  },
  { deep: true }
)

createSuggestionWatch(
  () => [props.isLoadingAccounts, props.mentionAccounts],
  accountLinkRenderer
)

createSuggestionWatch(
  () => [props.isLoadingReconTemplates, props.mentionReconTemplates],
  reconTemplateLinkRenderer
)

const autoFocus = () => {
  editor.value?.commands?.focus()
}

const editorWrapperClasses = computed(() => {
  return {
    'tw-rounded-b-none': props.squareBottom,
    '!tw-border-sf-danger-light-1': props.hasError,
    'tw-opacity-60 tw-pointer-events-none': props.disabled,
  }
})

const contentWrapperClasses = computed(() => {
  return {
    'tw-outline tw-outline-solid tw-outline-sf-primary tw-rounded-b-[0.2rem] tw-rounded-t-none':
      isFocused.value,
  }
})

defineExpose({
  autoFocus,
})
</script>

<style lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.tiptap {
  height: 100%;

  &:focus {
    outline: 0 solid transparent;
  }

  ul,
  ol {
    list-style-type: revert;
    margin: 0 0 10px 25px;
  }

  .mention[data-mention-suggestion-char='@'] {
    @include background-color('sky', 'subtle');
    @include font-weight('medium');

    color: -color('sky', 'dark');
    border-radius: 5px;
    padding: 2px 5px;
    box-decoration-break: clone;

    // Keep in visual sync with CHIP_CLASSES in use-pulse-mention-warning.ts.
    &.mention--pulse-warning {
      @include background-color('warning', 'subtle-2');

      color: -color('warning', 'default');
    }
  }

  .mention[data-mention-suggestion-char='#'],
  .mention[data-mention-suggestion-char='!'],
  .mention[data-mention-suggestion-char='/'] {
    color: -color('sky', 'default');
    cursor: pointer;

    &:hover {
      color: -color('sky', 'dark');
      text-decoration: underline !important;
      text-decoration-color: -color('sky', 'dark');
    }
  }

  // Liquid highlight: monospace + a distinct (non-sky) tint so template code
  // reads apart from @mentions and content links. Applied as an inline
  // decoration over plain text (liquid-decoration extension), not a node.
  .liquid {
    @include background-color('warning', 'subtle-2');

    color: -color('warning', 'default');
    border-radius: 4px;
    padding: 1px 4px;
    font-family: monospace;
    white-space: pre-wrap;
    box-decoration-break: clone;
  }

  // Placeholder styles
  p.is-editor-empty:first-child::before {
    color: -color('neutral', 'light-1');
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
}
</style>
